/**
 * DesignShapeMapSummary — read-only digest of the shared default Design
 * Shape map (Phase 56.1 / BLU-536 Plan 08).
 *
 * Sits under the "Advanced print mapping" disclosure, next to the shape
 * mapping rows it explains: when a variant's Design Shape metafield says
 * "Horizontal", THIS map turns it into the file code (H4, H6, ...) for the
 * product type + size.
 *
 * Deliberately plain (review-corrected): it states that it shows the SHARED
 * DEFAULT map — no "if your org has a custom map" conditional copy. Org-row
 * existence is a backend fact with no read endpoint this phase; the per-org
 * map editor is explicitly deferred.
 *
 * The digest derives from `DEFAULT_DESIGN_SHAPE_MAP` at render time (code
 * letters + product-type coverage are computed, not hardcoded) so a shared
 * map change can never leave this summary stale. Collapsible uses the same
 * Radix primitive + global tokenized motion as the Advanced disclosure
 * (index.css `[data-radix-collapsible-content]` rule — adding accordion
 * classes here would double-animate; reduced-motion override built in).
 */

import { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, Shapes } from 'lucide-react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';
import {
  DEFAULT_DESIGN_SHAPE_MAP,
  DESIGN_SHAPES,
  type DesignShape,
  type DesignShapeMap,
} from '@threadheads/shared';

export interface DesignShapeSummaryRow {
  shape: DesignShape;
  /** The code letter(s) this shape resolves to (e.g. "R"). */
  codeLetters: string;
  /** How many product types in the map carry a table for this shape. */
  productTypeCount: number;
}

/**
 * Pure digest of a Design Shape map: per shape, the distinct code letter
 * prefixes (digits stripped) and the number of product types covered.
 * Exported for the unit test.
 */
export function summarizeDesignShapeMap(
  map: DesignShapeMap,
): DesignShapeSummaryRow[] {
  return DESIGN_SHAPES.map((shape) => {
    const letters = new Set<string>();
    let productTypeCount = 0;
    for (const typeTable of Object.values(map)) {
      const sizeTable = typeTable[shape];
      if (!sizeTable) continue;
      productTypeCount += 1;
      for (const code of Object.values(sizeTable)) {
        const letter = code.replace(/[0-9]+$/, '');
        if (letter) letters.add(letter);
      }
    }
    return {
      shape,
      codeLetters: [...letters].sort().join(', '),
      productTypeCount,
    };
  });
}

export function DesignShapeMapSummary() {
  const [open, setOpen] = useState(false);

  const rows = useMemo(
    () => summarizeDesignShapeMap(DEFAULT_DESIGN_SHAPE_MAP),
    [],
  );
  const productTypeTotal = Object.keys(DEFAULT_DESIGN_SHAPE_MAP).length;

  return (
    <div className="rounded-md border bg-muted/20 p-3">
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="w-full justify-between px-1"
          >
            <span className="flex items-center gap-2 text-xs font-medium">
              <Shapes className="h-3.5 w-3.5" aria-hidden="true" />
              How shapes become file codes
            </span>
            {open ? (
              <ChevronUp className="h-3.5 w-3.5" aria-hidden="true" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
            )}
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="space-y-2 px-1 pt-2">
            <p className="text-xs text-muted-foreground">
              Shape values resolve through the shared default map: each shape
              crosses the product type and size to pick the file code in the
              print filename. This map is read-only here.
            </p>
            <ul className="space-y-1">
              {rows.map((row) => (
                <li
                  key={row.shape}
                  className="flex items-baseline gap-2 text-xs"
                >
                  <span className="w-24 shrink-0 font-medium">
                    {row.shape}
                  </span>
                  <span className="font-mono tabular-nums">
                    {row.codeLetters} codes
                  </span>
                  <span className="text-muted-foreground">
                    covers{' '}
                    <span className="tabular-nums">
                      {row.productTypeCount}
                    </span>{' '}
                    product types
                  </span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-muted-foreground">
              <span className="tabular-nums">{productTypeTotal}</span> product
              types in total. Anything outside these four shape names falls
              back to the code in the SKU.
            </p>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
